import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

// Sommige mailproviders/werkgevers scannen links in e-mails automatisch op
// veiligheid, en 'bezoeken' de resetlink daarmee zelf al vóórdat het lid 'm
// aanklikt. Omdat zo'n link maar één keer bruikbaar is, is die dan al
// 'verbruikt' tegen de tijd dat het lid er zelf op klikt — met "Auth session
// missing!" tot gevolg. Daarom bieden we naast de link ook een 6-cijferige
// code aan (uit dezelfde e-mail) die het lid hier zelf kan intypen; een code
// die alleen in platte tekst in een e-mail staat kan een scanner niet
// 'per ongeluk' verbruiken zoals een klikbare link dat wel kan.
export default function ResetWachtwoord() {
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [wachtwoord, setWachtwoord] = useState('')
  const [bevestig, setBevestig] = useState('')
  const [loading, setLoading] = useState(false)
  const [feedback, setFeedback] = useState('')
  const [klaar, setKlaar] = useState(false)
  const [sessieGeldig, setSessieGeldig] = useState(false)

  useEffect(() => {
    // Als de link zelf al is 'verbruikt' (bv. door een linkscanner), staat er
    // een foutmelding in de URL-hash — dan meteen naar de code-invoer i.p.v.
    // de gebruiker te laten wachten tot ze op 'Wachtwoord instellen' klikken.
    if (window.location.hash.includes('error=')) {
      setFeedback('Deze link is al gebruikt of verlopen (dit kan gebeuren doordat je mailprovider de link automatisch controleert). Vul hieronder de 6-cijferige code uit de e-mail in.')
    }

    // Race-conditie opvangen: als het PASSWORD_RECOVERY-event al vuurde vóórdat
    // deze listener actief was, staat de sessie er soms al wél — check dat apart.
    supabase.auth.getSession().then(({ data }) => {
      if (data?.session) setSessieGeldig(true)
    })

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
        setSessieGeldig(true)
      }
    })
    return () => sub?.subscription?.unsubscribe()
  }, [])

  async function handleSubmit(e) {
    e.preventDefault()
    if (wachtwoord.length < 6) {
      setFeedback('Wachtwoord moet minimaal 6 tekens zijn.')
      return
    }
    if (wachtwoord !== bevestig) {
      setFeedback('Wachtwoorden komen niet overeen.')
      return
    }
    if (!sessieGeldig && (!email.trim() || !code.trim())) {
      setFeedback('Vul je e-mailadres en de code uit de e-mail in.')
      return
    }
    setLoading(true)
    setFeedback('')
    try {
      if (!sessieGeldig) {
        const { error: otpError } = await supabase.auth.verifyOtp({
          email: email.trim().toLowerCase(),
          token: code.trim(),
          type: 'recovery',
        })
        if (otpError) throw otpError
      }
      const { error } = await supabase.auth.updateUser({ password: wachtwoord })
      if (error) throw error
      setKlaar(true)
    } catch (err) {
      setFeedback('Fout: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="login-page">
      <div style={{ position: 'relative', height: 180, flexShrink: 0 }}>
        <img src="/gym-header.jpg" alt="Vitaal Sterk" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 40%' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(30,36,22,0.45)' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 4, background: '#e76622' }} />
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', padding: '0 28px' }}>
          <img src="/logo.png" alt="Vitaal Sterk" style={{ height: 90, objectFit: 'contain' }} />
        </div>
      </div>

      <div className="login-body">
        {klaar ? (
          <div style={{ textAlign: 'center', paddingTop: 20 }}>
            <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <p style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Wachtwoord ingesteld!</p>
            <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 24 }}>Je kunt nu inloggen met je nieuwe wachtwoord.</p>
            <a href="/" style={{ color: 'var(--green)', fontSize: 13, fontWeight: 600 }}>← Ga naar de app</a>
          </div>
        ) : (
          <>
            <p style={{ fontFamily: 'Montserrat', fontSize: 11, color: 'var(--muted)', marginBottom: 24, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Stel je wachtwoord in
            </p>
            <form onSubmit={handleSubmit}>
              {!sessieGeldig && (
                <>
                  <div className="form-group">
                    <label className="form-label">E-mailadres</label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="jouw@email.nl" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Code uit de e-mail</label>
                    <input type="text" inputMode="numeric" value={code} onChange={e => setCode(e.target.value)} placeholder="6-cijferige code" />
                  </div>
                </>
              )}
              <div className="form-group">
                <label className="form-label">Nieuw wachtwoord</label>
                <input type="password" value={wachtwoord} onChange={e => setWachtwoord(e.target.value)} placeholder="Minimaal 6 tekens" />
              </div>
              <div className="form-group">
                <label className="form-label">Bevestig wachtwoord</label>
                <input type="password" value={bevestig} onChange={e => setBevestig(e.target.value)} placeholder="Herhaal wachtwoord" />
              </div>
              {feedback && <p className="error-msg">{feedback}</p>}
              <button className="btn-primary" style={{ marginTop: 18 }} disabled={loading}>
                {loading ? 'Bezig…' : 'Wachtwoord instellen'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
